// ./src/components/features/PartnerPackages/PartnerPackagesList/PartnerPackagesList.tsx
//
// Partner Packages list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Mirrors the Volunteer Roles list conventions (see em-scaffold-ui).

import React, { useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { PartnerPackage, partnerKindMeta, PARTNER_KIND_LABELS, PARTNER_MONEY_DIRECTION_LABELS } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalPartnerPackagesContext } from "@/contexts/PartnerPackagesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface PartnerPackagesListProps {
  data?: PartnerPackage[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditPackage?: (pkg: PartnerPackage) => void;
  onDeletePackage?: (pkg: PartnerPackage) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddPackage?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search/filter is active — shows a "no match" empty state instead of the "add first" one. */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (pkg: PartnerPackage): boolean => pkg.status === STATUSES.DISABLED;

const formatMoney =(amount?: number | null, currency?: string | null): string | null => {
  if (amount == null) {
    return null;
  }

  const value = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${value}` : value;
};

const StatusBadge: React.FC<{ pkg: PartnerPackage }> = ({ pkg }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(pkg)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const KindBadge: React.FC<{ pkg: PartnerPackage }> = ({ pkg }) => {
  const { t } = useTranslation(["partnerPackages"]);
  const meta = partnerKindMeta(pkg.partnerKind);

  if (!pkg.partnerKind) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color={meta.color} leftSection={<AppIcon iconName={meta.icon} size={10} />}>
      {t(`partnerPackages:kind.${pkg.partnerKind}`, {
        defaultValue: PARTNER_KIND_LABELS[pkg.partnerKind as keyof typeof PARTNER_KIND_LABELS] ?? pkg.partnerKind,
      })}
    </Badge>
  );
};

// What this package offers — money direction/amount + benefits count.
const DetailBadges: React.FC<{ pkg: PartnerPackage }> = ({ pkg }) => {
  const { t } = useTranslation(["partnerPackages"]);

  const money = formatMoney(pkg.defaultAmount, pkg.currencyCode);
  const benefitsCount = Array.isArray(pkg.benefits) ? pkg.benefits.length : 0;

  if (!money && benefitsCount === 0) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }

  const moneyColor = pkg.moneyDirection === "expense" ? "orange" : pkg.moneyDirection === "none" ? "gray" : "green";

  return (
    <Group gap={6} wrap="wrap">
      {money && (
        <Badge size="xs" variant="light" color={moneyColor} leftSection={<AppIcon iconName="Coin" size={10} />}>
          {money}
          {pkg.moneyDirection && pkg.moneyDirection !== "none"
            ? ` · ${t(`partnerPackages:money.${pkg.moneyDirection}`, {
                defaultValue:
                  PARTNER_MONEY_DIRECTION_LABELS[pkg.moneyDirection as keyof typeof PARTNER_MONEY_DIRECTION_LABELS] ??
                  pkg.moneyDirection,
              })}`
            : ""}
        </Badge>
      )}
      {benefitsCount > 0 && (
        <Badge size="xs" variant="outline" color="grape" leftSection={<AppIcon iconName="ListCheck" size={10} />}>
          {t("partnerPackages:detail.benefitsCount", { defaultValue: "{{count}} benefits", count: benefitsCount })}
        </Badge>
      )}
    </Group>
  );
};

const PackageIcon: React.FC<{ pkg: PartnerPackage; size?: number }> = ({ pkg, size = 34 }) => {
  const meta = partnerKindMeta(pkg.partnerKind);

  return (
    <ThemeIcon variant="light" color={meta.color} radius="xl" size={size}>
      <AppIcon iconName={meta.icon} size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
};

const RowActions: React.FC<{
  pkg: PartnerPackage;
  onEdit?: (r: PartnerPackage) => void;
  onDelete?: (r: PartnerPackage) => void;
  isDeleting: boolean;
}> = ({ pkg, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(pkg, "edit");
  const showDelete = !!onDelete && recordHasRight(pkg, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(pkg)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(pkg)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  pkg: PartnerPackage;
  name: string;
  disabled: boolean;
  onEdit?: (r: PartnerPackage) => void;
  onDelete?: (r: PartnerPackage) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const PackageTableRow: React.FC<RowProps> = ({
  pkg,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => pkg.id && onToggleSelect?.(pkg.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <PackageIcon pkg={pkg} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <KindBadge pkg={pkg} />
              <StatusBadge pkg={pkg} />
            </Group>
            {pkg.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {pkg.description}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 320 }}>
        <DetailBadges pkg={pkg} />
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(pkg.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions pkg={pkg} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const PackageTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 320 }}>
      <Skeleton height={18} width={140} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const PackageCardComponent: React.FC<CardProps> = ({
  pkg,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => pkg.id && onToggleSelect?.(pkg.id);

  const swipeActions = React.useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(pkg, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(pkg),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(pkg, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(pkg),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, pkg, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <PackageIcon pkg={pkg} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge pkg={pkg} />
          </Group>
          <Box mt={4}>
            <KindBadge pkg={pkg} />
          </Box>
          <Box mt={6}>
            <DetailBadges pkg={pkg} />
          </Box>
        </Box>
        {showActions && <RowActions pkg={pkg} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const PackageCard = React.memo(PackageCardComponent);

const PackageCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "md", "xs"]} gap={5} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const PartnerPackagesListComponent: React.FC<PartnerPackagesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditPackage,
  onDeletePackage,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddPackage,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["partnerPackages", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditPackage, onDeletePackage);

  const packagesContext = useOptionalPartnerPackagesContext();

  const packages = dataProp ?? packagesContext?.partnerPackages ?? [];
  const loading = loadingProp ?? packagesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? packagesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? packagesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? packagesContext?.loadMore;
  const isRemoving = isRemovingProp ?? packagesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: packages.length });

  const handleEdit = useCallback(
    (pkg: PartnerPackage) => {
      closeAllCards();
      onEditPackage?.(pkg);
    },
    [onEditPackage, closeAllCards]
  );

  const handleDelete = useCallback(
    (pkg: PartnerPackage) => {
      closeAllCards();
      onDeletePackage?.(pkg);
    },
    [onDeletePackage, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = packagesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && packages.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("partnerPackages:emptyState.noMatchTitle", { defaultValue: "No packages match" })}
            description={t("partnerPackages:emptyState.noMatchDescription", {
              defaultValue: "Try a different search or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("partnerPackages:emptyState.title", { defaultValue: "No partner packages yet" })}
          description={t("partnerPackages:emptyState.description", {
            defaultValue: "Define reusable sponsor / partner / exhibitor offers to use across your events.",
          })}
          iconName="Award"
          actionLabel={t("partnerPackages:emptyState.actionLabel", { defaultValue: "Add package" })}
          onAction={onAddPackage}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (pkg: PartnerPackage): boolean =>
    isSelectable &&
    ((!!onEditPackage && recordHasRight(pkg, "edit")) || (!!onDeletePackage && recordHasRight(pkg, "remove")));

  const rowProps = (pkg: PartnerPackage): RowProps => ({
    pkg,
    name: pkg.name || "—",
    disabled: isDisabled(pkg),
    onEdit: onEditPackage ? handleEdit : undefined,
    onDelete: onDeletePackage ? handleDelete : undefined,
    highlighted: pkg.id === highlightedId,
    isDeleting: isRemoving(pkg.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(pkg),
    selected: selection?.isSelected(pkg.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = packages
    .filter((r) => rowIsSelectable(r))
    .map((r) => r.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("partnerPackages:list.columns.name", { defaultValue: "Package" })}</Table.Th>
                <Table.Th style={{ width: 320 }}>
                  {t("partnerPackages:list.columns.details", { defaultValue: "Offer" })}
                </Table.Th>
                <Table.Th style={{ width: 170 }}>
                  {t("partnerPackages:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <PackageTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : packages.map((pkg) => <PackageTableRow key={pkg.id} {...rowProps(pkg)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <PackageCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {packages.map((pkg) => (
            <motion.div
              key={pkg.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <PackageCard
                {...rowProps(pkg)}
                forceClose={shouldForceClose(pkg.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(pkg.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(pkg.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const PartnerPackagesList = React.memo(PartnerPackagesListComponent);
